import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent,
} from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#targetAddToCartButton = this.#findMainAddToCartButton();

    this.#setupIntersectionObserver();

    const section = this.closest('.shopify-section');

    section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Locate the product form for this sticky bar.
   */
  #getProductForm() {
    const productId = this.dataset.productId;

    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace(
      'shopify-section-',
      ''
    );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Locate the real Add to cart button in the original product form.
   */
  #findMainAddToCartButton() {
    const productForm = this.#getProductForm();

    if (!productForm) return null;

    return productForm.querySelector(
      'button[ref="addToCartButton"][type="submit"][name="add"]'
    );
  }

  /**
   * Show the sticky bar when the original Add to cart button
   * has scrolled above the viewport.
   *
   * The document capture listener also handles nested scroll containers.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) {
      console.warn(
        '[sticky-add-to-cart] Product form not found.'
      );
      return;
    }

    const mainButton = this.#findMainAddToCartButton();

    if (!mainButton) {
      console.warn(
        '[sticky-add-to-cart] Original Add to cart button not found.'
      );
      return;
    }

    this.#targetAddToCartButton = mainButton;

    const updateStickyVisibility = () => {
      if (!this.isConnected) return;

      const currentButton = this.#findMainAddToCartButton();

      if (!currentButton) {
        this.#hideStickyBar();
        return;
      }

      this.#targetAddToCartButton = currentButton;

      const rect = currentButton.getBoundingClientRect();
      const buttonHasPassed = rect.bottom <= 0;
      const variantAvailable =
        this.dataset.variantAvailable === 'true' &&
        !currentButton.disabled;

      if (buttonHasPassed && variantAvailable) {
        this.#showStickyBar();
      } else {
        this.#hideStickyBar();
      }
    };

    const { signal } = this.#abortController;

    document.addEventListener(
      'scroll',
      updateStickyVisibility,
      {
        passive: true,
        capture: true,
        signal,
      }
    );

    window.addEventListener(
      'scroll',
      updateStickyVisibility,
      {
        passive: true,
        signal,
      }
    );

    window.addEventListener(
      'resize',
      updateStickyVisibility,
      {
        passive: true,
        signal,
      }
    );

    updateStickyVisibility();
    requestAnimationFrame(updateStickyVisibility);
  }

  /**
   * Add to cart through Horizon's original product button.
   * This keeps the existing cart drawer and theme behavior.
   */
  handleAddToCartClick = async () => {
    const productForm = this.#getProductForm();
    const targetButton = this.#findMainAddToCartButton();

    if (!productForm || !targetButton) {
      console.error(
        '[sticky-add-to-cart] Cannot find the original product form or Add to cart button.',
        {
          productId: this.dataset.productId,
          sectionId: this.closest('.shopify-section')?.id,
        }
      );
      return;
    }

    if (targetButton.disabled) return;

    this.#targetAddToCartButton = targetButton;
    targetButton.dataset.puppet = 'true';
    targetButton.click();

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) {
      return;
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement,
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Buy Now submits the currently selected variant to Shopify checkout.
   * It uses a separate Shopify product form and does not trigger Add to cart.
   */
  handleBuyNowClick = () => {
    const stickyForm = this.querySelector(
      '.sticky-add-to-cart__buy-now-form'
    );

    const productFormComponent = this.#getProductForm();

    const mainForm = productFormComponent?.querySelector(
      'form[data-type="add-to-cart-form"]'
    );

    const mainButton = this.#findMainAddToCartButton();

    const sourceVariantInput = mainForm?.querySelector(
      'input[name="id"]'
    );

    const sourceQuantityInput = mainForm?.querySelector(
      'input[name="quantity"]'
    );

    const targetVariantInput = stickyForm?.querySelector(
      'input[name="id"]'
    );

    const targetQuantityInput = stickyForm?.querySelector(
      'input[name="quantity"]'
    );

    if (
      !stickyForm ||
      !mainForm ||
      !mainButton ||
      mainButton.disabled ||
      !sourceVariantInput?.value ||
      !targetVariantInput ||
      !targetQuantityInput
    ) {
      console.warn(
        '[sticky-add-to-cart] Buy Now could not find a valid product form, variant, or quantity.'
      );
      return;
    }

    // Always use the variant selected in the original product form.
    targetVariantInput.value = sourceVariantInput.value;

    const quantity = Math.max(
      1,
      parseInt(
        sourceQuantityInput?.value ||
          this.dataset.initialQuantity ||
          '1',
        10
      ) || 1
    );

    targetQuantityInput.value = String(quantity);

    // Remove properties copied by any earlier click.
    stickyForm
      .querySelectorAll('[data-sticky-copied-field="true"]')
      .forEach((field) => field.remove());

    // Copy common text-based line item properties and selling plan.
    const sourceData = new FormData(mainForm);

    for (const [name, value] of sourceData.entries()) {
      const isProperty = name.startsWith('properties[');
      const isSellingPlan = name === 'selling_plan';

      if (!isProperty && !isSellingPlan) continue;
      if (typeof value !== 'string' || value === '') continue;

      const hiddenInput = document.createElement('input');

      hiddenInput.type = 'hidden';
      hiddenInput.name = name;
      hiddenInput.value = value;
      hiddenInput.dataset.stickyCopiedField = 'true';

      stickyForm.appendChild(hiddenInput);
    }

    // Submit the Buy Now form, which returns the customer to checkout.
    stickyForm.requestSubmit();
  };

  /**
   * Update the sticky bar after a variant selection.
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    if (!event.promise || typeof event.promise.then !== 'function') {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (
          productId &&
          productId.toString() !== this.dataset.productId
        ) {
          return;
        }

        const newStickyElement =
          html.querySelector('sticky-add-to-cart');

        const newStickyBar =
          newStickyElement?.querySelector('[ref="stickyBar"]');

        if (!newStickyElement || !newStickyBar) return;

        const currentStuck =
          this.refs.stickyBar.getAttribute('data-stuck') || 'false';

        const variantAvailable =
          newStickyElement.dataset.variantAvailable;

        morph(this.refs.stickyBar, newStickyBar, {
          childrenOnly: true,
        });

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable = variantAvailable;

        if (variant?.id) {
          this.dataset.currentVariantId = variant.id;
        }

        this.#targetAddToCartButton =
          this.#findMainAddToCartButton();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Variant update failed:',
            error
          );
        }
      });
  };

  /**
   * Show selected options if the current variant is unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector(
      '.sticky-add-to-cart__variant'
    );

    const productId = this.dataset.productId;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Reset visual state after the cart operation settles.
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      const currentButton = this.#findMainAddToCartButton();

      if (currentButton) {
        currentButton.dataset.puppet = 'false';
      }

      this.#targetAddToCartButton = currentButton;
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handle quantity changes from the product page.
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(this.dataset.initialQuantity || '1', 10) || 1;

    this.#updateButtonText();
  }

  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    if (
      !addToCartButton ||
      !quantityDisplay ||
      !quantityNumber
    ) {
      return;
    }

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}